import React, { useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useProducts } from './useProducts';
import { buildCategoryGroupCards, countProductsInGroup, thumbForGroup } from './catalogGroupUtils';
import { firstProductImage } from './catalog/catalogMedia';
import CollectionGroupCard from './components/CollectionGroupCard';
import { formatDisplayTitle } from './formatProductTitle';
import { categoryHeroBlurb, categoryHeroTagline } from './categoryHeroText';
import { shopCategoryAllPath } from './productPaths';

/** Landing page for any category slug (e.g. /shop/rakhi, /shop/gifts). */
const DynamicCategoryHub = () => {
  const { categorySlug } = useParams();
  const { products, categories, loading } = useProducts();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [categorySlug]);

  const meta = categories.find((c) => c.slug === categorySlug);
  const list = useMemo(() => products[categorySlug] || [], [products, categorySlug]);

  const groupCards = useMemo(() => buildCategoryGroupCards(meta, list), [meta, list]);

  const hubTagline = categoryHeroTagline(meta, '');
  const defaultHeroBlurb =
    list.length > 0
      ? `${list.length} handcrafted design${list.length === 1 ? '' : 's'} — explore styles below.`
      : 'New pieces are added often — please check back soon.';

  if (loading && groupCards.length === 0 && !list.length) {
    return (
      <div className="container py-5 text-center">
        <p className="text-muted">Loading collection…</p>
      </div>
    );
  }

  const title = formatDisplayTitle(meta?.name || categorySlug.replace(/-/g, ' '));

  if (!list.length) {
    return (
      <div className="collections-page collections-page--v2">
        <div className="container store-page__container">
          <section className="collections-hero collections-hero--v2">
            <div className="collections-hero-panel text-center">
              <nav className="collections-breadcrumb small mb-2" aria-label="Breadcrumb">
                <Link to="/shop">Shop</Link>
                <span aria-hidden> / </span>
                <span>{title}</span>
              </nav>
              <h1 className="collections-page-title">{title}</h1>
              {hubTagline ? <p className="collections-hero-tagline">{hubTagline}</p> : null}
              <p className="collections-page-subtitle collections-page-subtitle--editorial">
                {categoryHeroBlurb(meta, defaultHeroBlurb)}
              </p>
            </div>
          </section>
          <section className="collections-grid-section">
            <div className="text-center py-5">
              <p className="text-muted mb-3">No products in this category yet.</p>
              <Link to="/shop" className="btn btn-outline-primary">
                Back to shop
              </Link>
            </div>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className="collections-page collections-page--v2">
      <div className="container store-page__container">
        <section className="collections-hero collections-hero--v2">
          <div className="collections-hero-panel text-center">
            <nav className="collections-breadcrumb small mb-2" aria-label="Breadcrumb">
              <Link to="/shop">Shop</Link>
              <span aria-hidden> / </span>
              <span>{title}</span>
            </nav>
            <h1 className="collections-page-title">{title}</h1>
            {hubTagline ? <p className="collections-hero-tagline">{hubTagline}</p> : null}
            <p className="collections-page-subtitle collections-page-subtitle--editorial">
              {categoryHeroBlurb(meta, defaultHeroBlurb)}
            </p>
            <Link
              to={shopCategoryAllPath(categorySlug)}
              className="btn btn-outline-secondary btn-sm rounded-pill collections-hero__view-all"
            >
              View all {list.length} designs
            </Link>
          </div>
        </section>

        <section className="collections-grid-section">
          {groupCards.length === 0 ? (
            <div className="col-12 text-center py-4">
              <Link to={shopCategoryAllPath(categorySlug)} className="btn btn-primary btn-lg">
                View all products
              </Link>
            </div>
          ) : (
            <div className="collection-group-grid">
              {groupCards.map((card) => {
                const thumb = thumbForGroup(list, card.key) || firstProductImage(list);
                const n = countProductsInGroup(list, card.key);
                return (
                  <CollectionGroupCard
                    key={card.key}
                    to={`/shop/${categorySlug}/${encodeURIComponent(card.key)}`}
                    image={thumb}
                    title={card.label}
                    countLabel={n > 0 ? `${n} design${n === 1 ? '' : 's'}` : 'Coming soon'}
                  />
                );
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default DynamicCategoryHub;
